import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Checkbox, InputLabel, Autocomplete, TextField } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import { getFilterDrawer } from '../shared/FilterDrawer.jsx';

class ReportFilterDrawer extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @returns {function(object): React.ReactElement}
     */
    static getReactComponent($) {
        return () => {
            // Set default values for the date selectors
            const newDate = new Date();
            const newDateMinusNinetyDays = new Date(Date.now() - 7776000000);

            // Variables declaration
            const [releaseGroups, setReleaseGroups] = useState([]);
            const [releaseCodes, setReleaseCodes] = useState([]);
            const [companyCodes, setCompanyCodes] = useState([]);
            const [plants, setPlants] = useState([]);
            const [purchasingGroups, setPurchasingGroups] = useState([]);
            const [purchasingOrgs, setPurchasingOrgs] = useState([]);
            const [processingStartDate, setProcessingStartDate] = useState(
                newDateMinusNinetyDays.toISOString().substring(0, 10)
            );
            const [processingEndDate, setProcessingEndDate] = useState(newDate.toISOString().substring(0, 10));
            const [documentStartDate, setDocumentStartDate] = useState(
                newDateMinusNinetyDays.toISOString().substring(0, 10)
            );
            const [documentEndDate, setDocumentEndDate] = useState(newDate.toISOString().substring(0, 10));
            const [poChecked, setPOChecked] = useState(true);
            const [prChecked, setPRChecked] = useState(true);
            const [sapChecked, setSAPChecked] = useState(true);
            const [afsChecked, setAFSChecked] = useState(true);
            const [formInvalid, setFormInvalid] = useState(false);

            useEffect(() => {
                if (
                    (processingStartDate === '' && processingEndDate !== '') ||
                    (processingStartDate !== '' && processingEndDate === '')
                ) {
                    setFormInvalid(true);
                } else if (
                    (documentStartDate === '' && documentEndDate !== '') ||
                    (documentStartDate !== '' && documentEndDate === '')
                ) {
                    setFormInvalid(true);
                } else if (documentStartDate > documentEndDate || processingStartDate > processingEndDate) {
                    setFormInvalid(true);
                } else {
                    setFormInvalid(false);
                }
            }, [processingStartDate, processingEndDate, documentStartDate, documentEndDate]);

            // listen to properties
            const open = useProperty($, 'open');
            const data = useProperty($, 'filterValues') || {};
            const section = useProperty($, 'section') || [];

            // Close popup method
            const toggleDrawer = (open) => {
                return (event) => {
                    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
                        return;
                    }

                    $.set('open', open);
                };
            };

            // Forward data to draw & close the popup when applying
            const onApply = (event) => {
                const filters = {
                    releaseGroups,
                    releaseCodes,
                    poChecked,
                    prChecked,
                    sapChecked,
                    afsChecked,
                    companyCodes,
                    purchasingGroups,
                    purchasingOrgs,
                };
                if (section === 'approversChanged') {
                    filters.processingStartDate = processingStartDate;
                    filters.processingEndDate = processingEndDate;
                    filters.documentStartDate = documentStartDate;
                    filters.documentEndDate = documentEndDate;
                }
                if (section === 'missingConfiguration') {
                    filters.plants = plants;
                }
                $.set('selectedFilters-' + section, filters);

                toggleDrawer(false)(event);
            };

            // Display the form
            const getFilterList = () => (
                <Box role="presentation" onClose={toggleDrawer(false)} sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InputLabel>Document Type</InputLabel>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    margin: 0,
                                    padding: 0,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            onChange={(e) => setPOChecked(e.target.checked)}
                                            checked={poChecked}
                                        />
                                    }
                                    label="PO / Contract"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            onChange={(e) => setPRChecked(e.target.checked)}
                                            checked={prChecked}
                                        />
                                    }
                                    label="PR"
                                />
                            </Box>
                        </Grid>

                        {section === 'missingConfiguration' && (
                            <Grid item xs={12}>
                                <InputLabel>Approval Process</InputLabel>
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        margin: 0,
                                        padding: 0,
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(e) => setSAPChecked(e.target.checked)}
                                                checked={sapChecked}
                                            />
                                        }
                                        label="SAP Workflow"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(e) => setAFSChecked(e.target.checked)}
                                                checked={afsChecked}
                                            />
                                        }
                                        label="AFS"
                                    />
                                </Box>
                            </Grid>
                        )}

                        {section === 'approversChanged' && (
                            <Grid item xs={6}>
                                <TextField
                                    type="date"
                                    label="Processing Start Date"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => {
                                        setProcessingStartDate(e.target.value);
                                    }}
                                    value={processingStartDate}
                                    fullWidth
                                />
                            </Grid>
                        )}
                        {section === 'approversChanged' && (
                            <Grid item xs={6}>
                                <TextField
                                    type="date"
                                    label="Processing End Date"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => {
                                        setProcessingEndDate(e.target.value);
                                    }}
                                    value={processingEndDate}
                                    fullWidth
                                />
                            </Grid>
                        )}

                        {section === 'approversChanged' && (
                            <Grid item xs={6}>
                                <TextField
                                    type="date"
                                    label="Document Start Date"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => {
                                        setDocumentStartDate(e.target.value);
                                    }}
                                    value={documentStartDate}
                                    fullWidth
                                />
                            </Grid>
                        )}
                        {section === 'approversChanged' && (
                            <Grid item xs={6}>
                                <TextField
                                    type="date"
                                    label="Document End Date"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => {
                                        setDocumentEndDate(e.target.value);
                                    }}
                                    value={documentEndDate}
                                    fullWidth
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Autocomplete
                                id="purchasing-orgs"
                                options={data.PurchaseOrg || []}
                                getOptionLabel={(option) => option?.Ekorg + ' - ' + option?.Ekotx}
                                renderInput={(params) => <TextField {...params} label="Purchasing Organizations" />}
                                onChange={(e, v) => setPurchasingOrgs(v)}
                                value={purchasingOrgs || []}
                                fullWidth
                                multiple
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                id="company-code"
                                options={data.CompanyCode || []}
                                getOptionLabel={(option) => option?.CompCode + ' - ' + option?.CompCodeDesc}
                                renderInput={(params) => <TextField {...params} label="Company Code" />}
                                onChange={(e, v) => setCompanyCodes(v)}
                                value={companyCodes || []}
                                fullWidth
                                multiple
                            />
                        </Grid>

                        {section === 'missingConfiguration' && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="plants"
                                    options={data.Plants || []}
                                    getOptionLabel={(option) => option?.PlantCode + ' - ' + option?.PlantName}
                                    renderInput={(params) => <TextField {...params} label="Plant" />}
                                    onChange={(e, v) => setPlants(v)}
                                    value={plants || []}
                                    fullWidth
                                    multiple
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Autocomplete
                                id="purchasing-groups"
                                options={data.PurchaseGroup || []}
                                getOptionLabel={(option) => option?.PurchGroup + ' - ' + option?.PurchGroupName}
                                renderInput={(params) => <TextField {...params} label="Purchasing groups" />}
                                onChange={(e, v) => setPurchasingGroups(v)}
                                value={purchasingGroups || []}
                                fullWidth
                                multiple
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                id="release-group"
                                options={data.ReleaseGroup || []}
                                getOptionLabel={(option) => option?.Frggr + ' - ' + option?.Frggt}
                                renderInput={(params) => <TextField {...params} label="Release Group" />}
                                onChange={(e, v) => setReleaseGroups(v)}
                                value={releaseGroups || []}
                                fullWidth
                                multiple
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                id="release-code"
                                options={
                                    data.ReleaseCode?.filter((rc) =>
                                        releaseGroups.length === 0
                                            ? true
                                            : releaseGroups?.find((rg) => rg.Frggr === rc.Frggr) !== undefined
                                    ) || []
                                }
                                getOptionLabel={(option) =>
                                    option?.Frgco + ' - ' + option?.Frgct + ' (' + option?.Frggr + ')'
                                }
                                renderInput={(params) => <TextField {...params} label="Release Code" />}
                                onChange={(e, v) => setReleaseCodes(v)}
                                value={releaseCodes || []}
                                fullWidth
                                multiple
                            />
                        </Grid>
                    </Grid>
                </Box>
            );

            return getFilterDrawer(
                open,
                formInvalid,
                'The End Date should always be after the Start Date !',
                toggleDrawer,
                getFilterList,
                onApply,
                data
            );
        };
    }
}

registerBrick('01899b3c22dc60e5cc20', ReportFilterDrawer);
